import { type FC, useState, useCallback, useMemo } from "react";
import { Text } from "@/components/podkit/typography/Text";
import { Link } from "react-router-dom";
import { formatCredits } from "@/routes/settings/organization/enterprise-usage/enterprise-usage-helpers";
import { EditTeamBudgetDialog } from "@/routes/settings/organization/enterprise-usage/EditTeamBudgetDialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/flexkit/tables/Table";
import type { PlainMessage } from "@bufbuild/protobuf";
import type { TeamCumulativeCreditUsage } from "gitpod-next-api/gitpod/v1/billing_pb";

type PlainTeamCumulativeCreditUsage = PlainMessage<TeamCumulativeCreditUsage>;

type Props = {
    teams: PlainTeamCumulativeCreditUsage[];
    organizationId: string;
    /** e.g. "Since Jan 1, 2025" or "Since Jan 1, 2025 · Up to Feb 28, 2026" */
    periodLabel?: string;
};

export const TeamUsageTable: FC<Props> = ({ teams, organizationId, periodLabel }) => {
    const [editingTeam, setEditingTeam] = useState<PlainTeamCumulativeCreditUsage | null>(null);

    const sortedTeams = useMemo(
        () => [...teams].sort((a, b) => a.displayName.localeCompare(b.displayName)),
        [teams],
    );

    const handleEditClose = useCallback(() => setEditingTeam(null), []);

    if (sortedTeams.length === 0) {
        return (
            <div data-testid="team-usage-table">
                <Text className="text-sm text-content-tertiary">
                    No teams found. Create teams in Settings → Members to track team-level usage.
                </Text>
            </div>
        );
    }

    return (
        <div data-testid="team-usage-table">
            <div className="mb-3 flex items-center justify-between">
                <div>
                    <Text className="text-lg font-semibold text-content-primary">Team Credit Usage</Text>
                    {periodLabel && <Text className="mt-0.5 text-xs text-content-tertiary">{periodLabel}</Text>}
                </div>
            </div>

            <Table className="text-sm">
                <TableHeader className="border-b border-border-light [&_th]:bg-surface-secondary [&_th]:font-medium [&_th]:text-content-secondary">
                    <TableRow>
                        <TableHead className="text-left">Team</TableHead>
                        <TableHead className="text-right">Cumulative Usage</TableHead>
                        <TableHead className="text-right">Budget</TableHead>
                        <TableHead className="text-right">Budget used</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {sortedTeams.map((team) => {
                        // usage is a proto message field — always populated by the backend,
                        // but PlainMessage makes it optional. Defensive fallback to 0.
                        const totalCredits = team.usage?.totalCredits ?? 0;
                        // creditBudget is `optional int64` in proto — undefined means no allocation exists.
                        const budget = team.creditBudget !== undefined ? Number(team.creditBudget) : undefined;
                        const budgetPct = budget && budget > 0 ? Math.round((totalCredits / budget) * 100) : undefined;
                        const isOverBudget = budgetPct !== undefined && budgetPct > 100;

                        return (
                            <TableRow
                                key={team.teamId}
                                className="border-b border-border-light last:border-b-0 hover:bg-surface-hover"
                            >
                                <TableCell className="py-3">
                                    <div className="flex items-center gap-2">
                                        <span className="font-medium text-content-primary">{team.displayName}</span>
                                        <Link
                                            to={`/settings/members/team/${team.teamId}`}
                                            className="text-xs text-content-tertiary underline hover:text-content-secondary"
                                            data-tracking-id="team-usage-manage-link"
                                        >
                                            Manage
                                        </Link>
                                    </div>
                                </TableCell>
                                <TableCell className="py-3 text-right">
                                    <span className="font-medium text-content-primary">
                                        {formatCredits(totalCredits)}
                                    </span>
                                    <span className="ml-1 text-content-tertiary">credits</span>
                                </TableCell>
                                <TableCell className="py-3 text-right">
                                    {budget !== undefined ? (
                                        <button
                                            type="button"
                                            className="cursor-pointer text-content-primary hover:text-content-secondary"
                                            onClick={() => setEditingTeam(team)}
                                            data-tracking-id="team-usage-edit-budget"
                                        >
                                            <span className="font-medium underline decoration-dotted">
                                                {formatCredits(budget)}
                                            </span>
                                            <span className="ml-1 text-content-tertiary">credits</span>
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            className="cursor-pointer text-content-tertiary underline decoration-dotted hover:text-content-secondary hover:no-underline"
                                            onClick={() => setEditingTeam(team)}
                                            data-tracking-id="team-usage-set-budget"
                                        >
                                            Set budget
                                        </button>
                                    )}
                                </TableCell>
                                <TableCell className="py-3 text-right">
                                    {budgetPct !== undefined ? (
                                        <span
                                            className={
                                                isOverBudget
                                                    ? "text-content-destructive font-semibold"
                                                    : "text-content-primary"
                                            }
                                        >
                                            {budgetPct}%
                                        </span>
                                    ) : (
                                        <span className="text-content-tertiary">&mdash;</span>
                                    )}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>

            <Text className="mt-2 text-xs text-content-tertiary">
                A member&apos;s credit usage counts toward a team only for the period they belong to it. Adding or
                removing members changes future attribution but does not affect historical data. Team budgets apply
                since the team was created. You can adjust a team&apos;s budget at any time to reflect changing needs.
            </Text>

            {editingTeam && (
                <EditTeamBudgetDialog
                    open={!!editingTeam}
                    onClose={handleEditClose}
                    organizationId={organizationId}
                    teamId={editingTeam.teamId}
                    teamName={editingTeam.displayName}
                    currentBudget={
                        // optional int64 — undefined means no allocation configured
                        editingTeam.creditBudget !== undefined ? Number(editingTeam.creditBudget) : undefined
                    }
                />
            )}
        </div>
    );
};
